import type {
	AttributesSavedData,
	BoxShapeConfigSavedData,
	BoxSolidMeshSavedData,
	CylinderShapeConfigSavedData,
	CylinderSolidMeshSavedData,
	ShapeConfigSavedData,
	SphereShapeConfigSavedData,
	SphereSolidMeshSavedData,
	TAnchorType,
	TSolidMeshSavedData,
} from "earthbrain-viewer";
import type { IObjectData, ObjectType } from "../types";

export const SIMPLE_STRUCTURE_TYPE = {
	PIPE: "pipe",
	BOX_CULVERT: "box-culvert",
	RETAINING_WALL: "retaining-wall",
	MANHOLE: "manhole",
	MANUAL: "manual",
} as const;

export const SIMPLE_STRUCTURE_TYPES = [
	SIMPLE_STRUCTURE_TYPE.PIPE,
	SIMPLE_STRUCTURE_TYPE.BOX_CULVERT,
	SIMPLE_STRUCTURE_TYPE.RETAINING_WALL,
	SIMPLE_STRUCTURE_TYPE.MANHOLE,
	SIMPLE_STRUCTURE_TYPE.MANUAL,
] as const;

export type TSimpleStructureType = (typeof SIMPLE_STRUCTURE_TYPES)[number];

export function isSimpleStructureType(
	value: unknown,
): value is TSimpleStructureType {
	return (
		typeof value === "string" &&
		SIMPLE_STRUCTURE_TYPES.some((type) => type === value)
	);
}

// Re-export Viewer types for convenience
export type {
	TSolidMeshSavedData,
	BoxSolidMeshSavedData,
	SphereSolidMeshSavedData,
	BoxShapeConfigSavedData,
	SphereShapeConfigSavedData,
	CylinderSolidMeshSavedData,
	CylinderShapeConfigSavedData,
	ShapeConfigSavedData,
	AttributesSavedData,
};

// Client extends Viewer's AttributesSavedData.
// `rotation` is the single source of truth for UI display (see JSDoc below).
// Kept optional for backward compat with records that predate D3D-731.
// name is stored at top-level, not in attributes (same as other objects)
export type TClientAttributes = AttributesSavedData & {
	/**
	 * Delta rotation XYZ (degrees) relative to the baseline orientation at
	 * the start of the most recent edit session. This is the single source of
	 * truth for UI display. When absent, the UI defaults to {x:0, y:0, z:0}.
	 * Both parametric and non-parametric store delta here; absolute orientation
	 * is encoded in shapeConfig.quaternion.
	 */
	rotation?: { x: number; y: number; z: number };
	/**
	 * Structure type for parametric simple structures, or MANUAL for custom meshes.
	 * Undefined means legacy/unclassified.
	 */
	structureType?: TSimpleStructureType;
	baselineQuaternion?: ShapeConfigSavedData["quaternion"];
};

/**
 * Check if the structure is a parametric type (non-manual preset)
 * Parametric structures have baseline rotation/scale that should be hidden from user
 */
export function isParametricStructure(
	structureType?: TSimpleStructureType,
): boolean {
	return !!structureType && structureType !== SIMPLE_STRUCTURE_TYPE.MANUAL;
}

export type IBoxObjectMetaData = {
	shapeConfig: BoxShapeConfigSavedData;
	attributes: TClientAttributes;
};

export type ISphereObjectMetaData = {
	shapeConfig: SphereShapeConfigSavedData;
	attributes: TClientAttributes;
};

export type ICylinderObjectMetaData = {
	shapeConfig: CylinderShapeConfigSavedData;
	attributes: TClientAttributes;
};

export type IShapeObjectMetaData =
	| IBoxObjectMetaData
	| ISphereObjectMetaData
	| ICylinderObjectMetaData;

export type ISolidMeshObjectMetaDataCompatible = {
	version?: 1.0 | 2.0;
	shapeConfig: ShapeConfigSavedData;
	attributes: TClientAttributes;
};

// ============================================
// Versioned MetaData types (for migration)
// SNAPSHOT DEFINITIONS - fully inlined, no type references
// If current type changes, compile error occurs
// ============================================

// Type equality check utility
type IsEqual<T, U> = [T] extends [U] ? ([U] extends [T] ? true : false) : false;

// Box V1 (snapshot — historical, rotation was required)
// id is stored at top-level, not in metaData
// name is stored at top-level, not in attributes
export type IBoxObjectMetaData_V1 = {
	version: 1.0;
	shapeConfig: {
		shapeType: "box";
		localCenter: { x: number; y: number; z: number };
		wgs84Center: { x: number; y: number; z: number };
		translation: { x: number; y: number; z: number };
		quaternion: { x: number; y: number; z: number; w: number };
		scale: { x: number; y: number; z: number };
		dimensions: { x: number; y: number; z: number };
		color: { red: number; green: number; blue: number; alpha: number };
	};
	attributes: {
		anchorType: TAnchorType;
		rotation: { x: number; y: number; z: number };
		structureType?: TSimpleStructureType;
	};
};

// Box V2 (rotation removed — baseline now in viewer memory)
export type IBoxObjectMetaData_V2 = {
	version: 2.0;
	shapeConfig: {
		shapeType: "box";
		localCenter: { x: number; y: number; z: number };
		wgs84Center: { x: number; y: number; z: number };
		translation: { x: number; y: number; z: number };
		quaternion: { x: number; y: number; z: number; w: number };
		scale: { x: number; y: number; z: number };
		dimensions: { x: number; y: number; z: number };
		color: { red: number; green: number; blue: number; alpha: number };
	};
	attributes: ISolidMeshObjectMetaDataCompatible["attributes"];
};

type _AssertBoxV2 = IsEqual<
	Omit<IBoxObjectMetaData_V2, "version">,
	IBoxObjectMetaData
> extends true
	? true
	: "ERROR: IBoxObjectMetaData changed - define new version";

export type TBoxVersioned = IBoxObjectMetaData_V1 | IBoxObjectMetaData_V2;

// Sphere V1 (snapshot — historical, rotation was required)
// id is stored at top-level, not in metaData
// name is stored at top-level, not in attributes
export type ISphereObjectMetaData_V1 = {
	version: 1.0;
	shapeConfig: {
		shapeType: "sphere";
		localCenter: { x: number; y: number; z: number };
		wgs84Center: { x: number; y: number; z: number };
		translation: { x: number; y: number; z: number };
		quaternion: { x: number; y: number; z: number; w: number };
		scale: { x: number; y: number; z: number };
		dimensions: { x: number; y: number; z: number };
		color: { red: number; green: number; blue: number; alpha: number };
	};
	attributes: {
		anchorType: TAnchorType;
		rotation: { x: number; y: number; z: number };
		structureType?: TSimpleStructureType;
	};
};

// Sphere V2 (rotation removed — baseline now in viewer memory)
export type ISphereObjectMetaData_V2 = {
	version: 2.0;
	shapeConfig: {
		shapeType: "sphere";
		localCenter: { x: number; y: number; z: number };
		wgs84Center: { x: number; y: number; z: number };
		translation: { x: number; y: number; z: number };
		quaternion: { x: number; y: number; z: number; w: number };
		scale: { x: number; y: number; z: number };
		dimensions: { x: number; y: number; z: number };
		color: { red: number; green: number; blue: number; alpha: number };
	};
	attributes: ISolidMeshObjectMetaDataCompatible["attributes"];
};

type _AssertSphereV2 = IsEqual<
	Omit<ISphereObjectMetaData_V2, "version">,
	ISphereObjectMetaData
> extends true
	? true
	: "ERROR: ISphereObjectMetaData changed - define new version";

export type TSphereVersioned =
	| ISphereObjectMetaData_V1
	| ISphereObjectMetaData_V2;

// Cylinder V1 (snapshot — historical, rotation was required)
// id is stored at top-level, not in metaData
// name is stored at top-level, not in attributes
export type ICylinderObjectMetaData_V1 = {
	version: 1.0;
	shapeConfig: {
		shapeType: "cylinder";
		localCenter: { x: number; y: number; z: number };
		wgs84Center: { x: number; y: number; z: number };
		translation: { x: number; y: number; z: number };
		quaternion: { x: number; y: number; z: number; w: number };
		scale: { x: number; y: number; z: number };
		dimensions: { x: number; y: number; z: number };
		color: { red: number; green: number; blue: number; alpha: number };
	};
	attributes: {
		anchorType: TAnchorType;
		rotation: { x: number; y: number; z: number };
		structureType?: TSimpleStructureType;
	};
};

// Cylinder V2 (rotation removed — baseline now in viewer memory)
export type ICylinderObjectMetaData_V2 = {
	version: 2.0;
	shapeConfig: {
		shapeType: "cylinder";
		localCenter: { x: number; y: number; z: number };
		wgs84Center: { x: number; y: number; z: number };
		translation: { x: number; y: number; z: number };
		quaternion: { x: number; y: number; z: number; w: number };
		scale: { x: number; y: number; z: number };
		dimensions: { x: number; y: number; z: number };
		color: { red: number; green: number; blue: number; alpha: number };
	};
	attributes: ISolidMeshObjectMetaDataCompatible["attributes"];
};

type _AssertCylinderV2 = IsEqual<
	Omit<ICylinderObjectMetaData_V2, "version">,
	ICylinderObjectMetaData
> extends true
	? true
	: "ERROR: ICylinderObjectMetaData changed - define new version";

export type TCylinderVersioned =
	| ICylinderObjectMetaData_V1
	| ICylinderObjectMetaData_V2;

// ============================================
// ObjectData types (for persistence)
// ============================================

export interface ISolidMeshObjectData extends IObjectData {
	type: ObjectType.SOLID_MESH;
	metaData: IShapeObjectMetaData;
}

/** DB payload: localCenter and associatedTerrain omitted */
export interface ISolidMeshObjectDataForDB
	extends Omit<ISolidMeshObjectData, "metaData" | "associatedTerrain"> {
	cid: string;
	metaData: {
		shapeConfig: Omit<ShapeConfigSavedData, "localCenter">;
		attributes: TClientAttributes;
	};
	version: number;
}
